import { ImageServiceType } from '@omni/kit/Types';
import { KitText } from '@omni/kit/components';
import { EventImage } from '@omni/kit/components/EventImage';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import {
  WindowSizeMin,
  useSizeClass,
} from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { dateFormatRange } from '@omni/kit/utilities/dateFormatRange';
import {
  SCREEN_WIDTH,
  createImageUrl,
  parseImageUrl,
} from '@omni/kit/utilities/utilities';
import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from '@react-navigation/native';
import moment from 'moment';
import React, { ReactElement, useContext, useMemo, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';

import { CustomMessageData, SendbirdUserMessage } from '../../../../../Types';
import { ChatContext } from '../../../../../mobile/scenes/channel/types';
import * as Constants from '../../../../Constants';

const debug = require('debug')(
  'omni:messaging:kit:scenes:channel:GroupEventMessage'
);

export default function GroupEventMessage({
  message,
  containerWidth,
}: {
  message: any;
  containerWidth: number;
}): ReactElement {
  const { t } = useTranslation();
  const { tokens } = useShellContext();
  const accessToken = useMemo(() => tokens.user, [tokens.user]);
  const data = message.data;

  // Navigation
  let navigation: NavigationProp<ParamListBase>;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigation = useNavigation();
  } catch {
    navigation = {} as NavigationProp<ParamListBase>; // Provide a fallback object
  }

  let startDateInEvent: string | undefined,
    endDateInEvent: string | undefined,
    imageURL,
    eventTitle,
    eventId: string;
  let eventTimezone: string | undefined;
  let isRepeatingEvent: boolean;
  let isAllDay: boolean | undefined;
  let eventDuration: number | undefined;
  const { smallestWindowWidth, windowHeight, windowWidth } = useSizeClass();
  const isSmall =
    Platform.OS === 'web'
      ? windowWidth < WindowSizeMin.L
      : smallestWindowWidth < WindowSizeMin.L;
  const isLandscape = windowHeight < windowWidth;
  const { handleSelectingMessage } = useContext(ChatContext);

  if (data) {
    try {
      const parsedData: CustomMessageData = JSON.parse(data);
      isRepeatingEvent = parsedData.groupEvent?.repetition_rules !== undefined;
      eventTimezone = parsedData.groupEvent?.timezone || undefined;
      isAllDay = parsedData.groupEvent?.all_day || undefined;
      eventId = parsedData.groupEvent?.id ?? '';
      startDateInEvent = parsedData.groupEvent?.start_at || undefined;
      endDateInEvent = parsedData.groupEvent?.end_at;
      eventDuration = parsedData.groupEvent?.event_duration;
      imageURL = parsedData.groupEvent?._embedded?.images?.[0].id;
      eventTitle = parsedData.groupEvent?.title;
    } catch (e) {
      debug('Error parsing group event in event message');
    }
  }

  // Calculate endDate if eventDuration is provided
  const endDateEvent = useMemo(() => {
    if (isAllDay) {
      // If all day is true, set the end date to 23:59:59 of the same day
      const startDateMoment = moment(startDateInEvent);

      return startDateMoment.endOf('day').toISOString();
    }

    if (startDateInEvent && eventDuration !== undefined) {
      // For events with a duration, calculate the end date by adding the duration to the start date
      const startDateMoment = moment(startDateInEvent);
      const endDateMoment = startDateMoment.add(eventDuration, 'minutes');

      return endDateMoment.toISOString();
    }

    return endDateInEvent;
  }, [startDateInEvent, eventDuration, endDateInEvent, isAllDay]);

  const mediaWidth = isSmall ? Math.round(containerWidth * 0.75) : 400;
  const mediaHeight = mediaWidth / 1.6;
  const endDate = moment(endDateEvent);
  const hasEnded = moment().isAfter(endDate);
  const imageUrl = parseImageUrl(
    createImageUrl(imageURL),
    mediaWidth,
    mediaHeight,
    ImageServiceType.ImageJpeg
  );

  // Navigation handler to go to the event details screen
  const handleEventPress = () => {
    if (eventId) {
      if ((Platform.OS === 'web' && !isSmall) || isLandscape) {
        // This will run for Web or Landscape orientation
        navigation.navigate('channel_details_screen', {
          screen: Constants.EVENT_DETAILS_SCREEN,
          params: { eventId: eventId, isRepeatingEvent: isRepeatingEvent },
        });
      } else {
        // This will run for Native (iOS/Android) or Portrait orientation
        navigation.navigate(Constants.EVENT_DETAILS_SCREEN, {
          eventId: eventId,
          isRepeatingEvent: isRepeatingEvent,
        });
      }
    }
  };

  return (
    <Pressable
      style={({ pressed }) => ({
        borderRadius: 20,
      })}
      onPress={() => handleEventPress}
    >
      <View style={styles.container}>
        <View style={styles.line} />
        <View
          style={{
            flexDirection: 'column',
            marginLeft: Spacing.m,
          }}
        >
          <TouchableOpacity
            onLongPress={handleSelectingMessage}
            onPress={() => handleEventPress()}
            style={{ width: mediaWidth }}
          >
            <EventImage
              url={imageUrl}
              date={startDateInEvent}
              timezone={eventTimezone}
              customWidth={imageUrl ? mediaWidth : 36}
              customHeight={imageUrl ? mediaHeight : 36}
            />
            <KitText
              numberOfLines={1}
              ellipsizeMode='tail'
              style={{
                maxWidth: mediaWidth,
                marginTop: Spacing.m,
                fontSize: 16,
                color: Colors.N900,
              }}
            >
              {eventTitle}
            </KitText>
            <KitText style={styles.eventTime}>
              {dateFormatRange({
                startDate: startDateInEvent ? startDateInEvent : '',
                endDate: endDateEvent,
                timezone: eventTimezone,
                allDay: isAllDay,
              })}
            </KitText>

            {hasEnded ? (
              <View style={styles.eventEnded}>
                <KitText fontSize={14} color={Colors.N500}>
                  {t('groups:endedEventText')}
                </KitText>
              </View>
            ) : null}
          </TouchableOpacity>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: Spacing.xs,
    flex: 1,
    minHeight: 50,
  },
  line: {
    width: 3,
    height: '100%',
    position: 'absolute',
    borderRadius: 1.5,
    backgroundColor: Colors.N150,
  },
  image: {
    marginLeft: 5,
    width: 50,
    height: 50,
  },
  eventTime: {
    fontSize: 14,
    color: Colors.N500,
  },
  eventEnded: {
    width: '100%',
    height: 50,
    backgroundColor: Colors.N100,
    borderRadius: 8,
    paddingHorizontal: 18,
    paddingVertical: 15,
    marginTop: 12,
    marginRight: 12,
  },
});
